import React from 'react';
import { X, CheckCircle, AlertCircle, Info, Clock } from 'lucide-react';

export interface NoticeAction {
  label: string;
  onClick: () => void;
  variant?: 'primary' | 'secondary';
}

export interface PersistentNotice {
  id: string;
  type: 'info' | 'success' | 'warning' | 'pending';
  title: string;
  message: string;
  dismissible?: boolean;
  actions?: NoticeAction[];
  persistenceKey?: string;
  autoExpire?: number; // milliseconds
}

interface NoticeBarProps {
  notice: PersistentNotice;
  onDismiss: (id: string) => void;
}

const NoticeBar: React.FC<NoticeBarProps> = ({ notice, onDismiss }) => {
  const getIcon = () => {
    switch (notice.type) {
      case 'success':
        return <CheckCircle className="h-5 w-5 text-neon-lime" />;
      case 'warning':
        return <AlertCircle className="h-5 w-5 text-warning-400" />;
      case 'pending':
        return <Clock className="h-5 w-5 text-neon-cyan" />;
      case 'info':
      default:
        return <Info className="h-5 w-5 text-neon-cyan" />;
    }
  };

  const getBackgroundColor = () => {
    switch (notice.type) {
      case 'success':
        return 'bg-neon-lime/[.08] border-neon-lime/25';
      case 'warning':
        return 'bg-warning-500/10 border-warning-500/30';
      case 'pending':
        return 'bg-neon-cyan/[.07] border-neon-cyan/25';
      case 'info':
      default:
        return 'bg-neon-cyan/[.07] border-neon-cyan/25';
    }
  };

  const getTitleColor = () => {
    switch (notice.type) {
      case 'success':
        return 'text-neon-lime';
      case 'warning':
        return 'text-warning-400';
      case 'pending':
      case 'info':
      default:
        return 'text-fg-bright';
    }
  };

  const getButtonStyles = () => {
    return 'text-fg-muted hover:text-fg-bright hover:bg-white/[.06]';
  };

  const getActionButtonStyles = (isPrimary: boolean) => {
    if (!isPrimary) {
      switch (notice.type) {
        case 'success':
          return 'text-neon-lime hover:bg-neon-lime/10';
        case 'warning':
          return 'text-warning-400 hover:bg-warning-500/15';
        case 'pending':
        case 'info':
        default:
          return 'text-neon-cyan hover:bg-neon-cyan/10';
      }
    }

    switch (notice.type) {
      case 'success':
        return 'bg-accept-gradient text-ink font-bold hover:opacity-90';
      case 'warning':
        return 'bg-warning-500 text-ink font-bold hover:opacity-90';
      case 'pending':
      case 'info':
      default:
        return 'bg-neon-gradient text-ink font-bold shadow-neon-pink hover:opacity-90';
    }
  };

  return (
    <div className={`border rounded-card p-6 mb-6 ${getBackgroundColor()} animate-slide-up`}>
      <div className="flex items-start">
        <div className="flex-shrink-0">
          {getIcon()}
        </div>
        <div className="ml-3 flex-1">
          <h4 className={`text-sm font-semibold ${getTitleColor()}`}>
            {notice.title}
          </h4>
          <p className="mt-2 text-sm text-fg-body leading-relaxed">
            {notice.message}
          </p>
          {notice.actions && notice.actions.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-3">
              {notice.actions.map((action, index) => (
                <button
                  key={index}
                  onClick={action.onClick}
                  className={`text-sm font-medium px-4 py-2 rounded-btn transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-neon-pink/50 ${getActionButtonStyles(action.variant === 'primary')}`}
                >
                  {action.label}
                </button>
              ))}
            </div>
          )}
        </div>
        {notice.dismissible !== false && (
          <div className="ml-4 flex-shrink-0">
            <button
              onClick={() => onDismiss(notice.id)}
              className={`inline-flex rounded-lg p-1.5 transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-neon-pink/50 ${getButtonStyles()}`}
            >
              <span className="sr-only">Sluiten</span>
              <X className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default NoticeBar;